import React, { useState } from 'react';
import { PlusIcon, TrashIcon } from '@heroicons/react/24/outline';
import {
  CreateSaleDto,
  SaleItem,
  PaymentMethod,
  getPaymentMethodDisplay,
  generateInvoiceNumber,
  calculateSaleTotal,
} from '../../api/sales';
import { BrandModal, BrandModalBody, BrandModalFooter, BrandModalHeader } from '../ui/BrandModal';
import AmountInput from '../ui/AmountInput';

interface CreateSaleModalProps {
  isOpen: boolean;
  onClose: () => void;
  onSubmit: (data: CreateSaleDto) => Promise<void>;
}

const CreateSaleModal: React.FC<CreateSaleModalProps> = ({
  isOpen,
  onClose,
  onSubmit,
}) => {
  const [formData, setFormData] = useState<Omit<CreateSaleDto, 'items'>>({
    invoiceNumber: generateInvoiceNumber(),
    saleDate: new Date().toISOString().split('T')[0],
    buyerName: '',
    buyerPhone: '',
    buyerAddress: '',
    paymentMethod: PaymentMethod.CASH,
    total: 0,
    notes: '',
  });

  const [items, setItems] = useState<SaleItem[]>([
    {
      productName: '',
      quantity: 1,
      unitPrice: 0,
      totalPrice: 0,
    }
  ]);

  const [loading, setLoading] = useState(false);

  const updateItemTotal = (index: number, quantity: number, unitPrice: number) => {
    const totalPrice = quantity * unitPrice;
    const updatedItems = [...items];
    updatedItems[index] = {
      ...updatedItems[index],
      quantity,
      unitPrice,
      totalPrice,
    };
    setItems(updatedItems);
    
    // Update total
    const newTotal = calculateSaleTotal(updatedItems);
    setFormData(prev => ({ ...prev, total: newTotal }));
  };

  const addItem = () => {
    setItems(prev => [...prev, {
      productName: '',
      quantity: 1,
      unitPrice: 0,
      totalPrice: 0,
    }]);
  };

  const removeItem = (index: number) => {
    if (items.length > 1) {
      const updatedItems = items.filter((_, i) => i !== index);
      setItems(updatedItems);
      
      // Update total
      const newTotal = calculateSaleTotal(updatedItems);
      setFormData(prev => ({ ...prev, total: newTotal }));
    }
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    
    if (loading) return;
    
    try {
      setLoading(true);
      
      const submitData: CreateSaleDto = {
        ...formData,
        items: items.filter(item => item.productName.trim() !== ''),
        saleDate: new Date(formData.saleDate),
      };

      await onSubmit(submitData);
      
      // Reset form
      setFormData({
        invoiceNumber: generateInvoiceNumber(),
        saleDate: new Date().toISOString().split('T')[0],
        buyerName: '',
        buyerPhone: '',
        buyerAddress: '',
        paymentMethod: PaymentMethod.CASH,
        total: 0,
        notes: '',
      });
      setItems([{
        productName: '',
        quantity: 1,
        unitPrice: 0,
        totalPrice: 0,
      }]);
    } catch (error) {
      console.error('Error creating sale:', error);
    } finally {
      setLoading(false);
    }
  };

  return (
    <BrandModal open={isOpen} onClose={onClose} maxWidthClass="max-w-4xl">
      <BrandModalHeader title="Create New Sale" onClose={onClose} />
      <form id="create-sale-form" onSubmit={handleSubmit}>
        <BrandModalBody>
              <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                {/* Left Column - Basic Information */}
                <div className="space-y-4">
                  <div>
                    <label htmlFor="invoiceNumber" className="block text-sm font-medium text-slate-700">
                      Invoice Number
                    </label>
                    <input
                      type="text"
                      id="invoiceNumber"
                      value={formData.invoiceNumber}
                      onChange={(e) => setFormData(prev => ({ ...prev, invoiceNumber: e.target.value }))}
                      className="mt-1 block w-full border-gray-300 rounded-md shadow-sm focus:ring-cyan-500 focus:border-cyan-500 sm:text-sm"
                      required
                    />
                  </div>

                  <div>
                    <label htmlFor="saleDate" className="block text-sm font-medium text-slate-700">
                      Sale Date
                    </label>
                    <input
                      type="date"
                      id="saleDate"
                      value={
                        formData.saleDate instanceof Date 
                          ? formData.saleDate.toISOString().split('T')[0]
                          : formData.saleDate
                      }
                      onChange={(e) => setFormData(prev => ({ ...prev, saleDate: e.target.value }))}
                      className="mt-1 block w-full border-gray-300 rounded-md shadow-sm focus:ring-cyan-500 focus:border-cyan-500 sm:text-sm"
                      required
                    />
                  </div>

                  <div>
                    <label htmlFor="paymentMethod" className="block text-sm font-medium text-slate-700">
                      Payment Method
                    </label>
                    <select
                      id="paymentMethod"
                      value={formData.paymentMethod}
                      onChange={(e) => setFormData(prev => ({ ...prev, paymentMethod: e.target.value as PaymentMethod }))}
                      className="mt-1 block w-full border-gray-300 rounded-md shadow-sm focus:ring-cyan-500 focus:border-cyan-500 sm:text-sm"
                      required
                    >
                      {Object.values(PaymentMethod).map(method => (
                        <option key={method} value={method}>
                          {getPaymentMethodDisplay(method)}
                        </option>
                      ))}
                    </select>
                  </div>
                </div>

                {/* Right Column - Customer Information */}
                <div className="space-y-4">
                  <div>
                    <label htmlFor="buyerName" className="block text-sm font-medium text-slate-700">
                      Customer Name *
                    </label>
                    <input
                      type="text"
                      id="buyerName"
                      value={formData.buyerName}
                      onChange={(e) => setFormData(prev => ({ ...prev, buyerName: e.target.value }))}
                      className="mt-1 block w-full border-gray-300 rounded-md shadow-sm focus:ring-cyan-500 focus:border-cyan-500 sm:text-sm"
                      required
                    />
                  </div>

                  <div>
                    <label htmlFor="buyerPhone" className="block text-sm font-medium text-slate-700">
                      Customer Phone
                    </label>
                    <input
                      type="tel"
                      id="buyerPhone"
                      value={formData.buyerPhone}
                      onChange={(e) => setFormData(prev => ({ ...prev, buyerPhone: e.target.value }))}
                      className="mt-1 block w-full border-gray-300 rounded-md shadow-sm focus:ring-cyan-500 focus:border-cyan-500 sm:text-sm"
                    />
                  </div>

                  <div>
                    <label htmlFor="buyerAddress" className="block text-sm font-medium text-slate-700">
                      Customer Address
                    </label>
                    <textarea
                      id="buyerAddress"
                      value={formData.buyerAddress}
                      onChange={(e) => setFormData(prev => ({ ...prev, buyerAddress: e.target.value }))}
                      rows={3}
                      className="mt-1 block w-full border-gray-300 rounded-md shadow-sm focus:ring-cyan-500 focus:border-cyan-500 sm:text-sm"
                    />
                  </div>
                </div>
              </div>

              {/* Items Section */}
              <div className="mt-6">
                <div className="flex items-center justify-between mb-4">
                  <h4 className="text-md font-medium text-slate-900">Sale Items</h4>
                  <button
                    type="button"
                    onClick={addItem}
                    className="inline-flex items-center px-3 py-1 border border-transparent text-sm font-medium rounded-md text-cyan-600 bg-cyan-100 hover:bg-indigo-200 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-cyan-500"
                  >
                    <PlusIcon className="h-4 w-4 mr-1" />
                    Add Item
                  </button>
                </div>

                <div className="space-y-3">
                  {items.map((item, index) => (
                    <div key={index} className="grid grid-cols-12 gap-3 items-end">
                      <div className="col-span-5">
                        <label className="block text-sm font-medium text-slate-700">Product Name</label>
                        <input
                          type="text"
                          value={item.productName}
                          onChange={(e) => {
                            const updatedItems = [...items];
                            updatedItems[index].productName = e.target.value;
                            setItems(updatedItems);
                          }}
                          className="mt-1 block w-full border-gray-300 rounded-md shadow-sm focus:ring-cyan-500 focus:border-cyan-500 sm:text-sm"
                          placeholder="Enter product name..."
                          required
                        />
                      </div>

                      <div className="col-span-2">
                        <label className="block text-sm font-medium text-slate-700">Quantity</label>
                        <input
                          type="number"
                          min="1"
                          value={item.quantity}
                          onChange={(e) => {
                            const quantity = parseInt(e.target.value) || 1;
                            updateItemTotal(index, quantity, item.unitPrice);
                          }}
                          className="mt-1 block w-full border-gray-300 rounded-md shadow-sm focus:ring-cyan-500 focus:border-cyan-500 sm:text-sm"
                        />
                      </div>

                      <div className="col-span-2">
                        <label className="block text-sm font-medium text-slate-700">Unit Price</label>
                        <AmountInput
                          value={item.unitPrice}
                          onChange={(v) => updateItemTotal(index, item.quantity, v ?? 0)}
                          min={0}
                          inputClassName="mt-1 block w-full border-gray-300 rounded-md shadow-sm focus:ring-cyan-500 focus:border-cyan-500 sm:text-sm"
                        />
                      </div>

                      <div className="col-span-2">
                        <label className="block text-sm font-medium text-slate-700">Total</label>
                        <input
                          type="number"
                          value={item.totalPrice.toFixed(2)}
                          readOnly
                          className="mt-1 block w-full border-gray-300 rounded-md shadow-sm bg-slate-50/80 sm:text-sm"
                        />
                      </div>

                      <div className="col-span-1">
                        <button
                          type="button"
                          onClick={() => removeItem(index)}
                          disabled={items.length === 1}
                          className="p-2 text-red-600 hover:text-red-800 disabled:text-slate-400 disabled:cursor-not-allowed"
                        >
                          <TrashIcon className="h-4 w-4" />
                        </button>
                      </div>
                    </div>
                  ))}
                </div>

                {/* Total Display */}
                <div className="mt-4 flex justify-end">
                  <div className="text-right">
                    <div className="text-sm text-slate-500">Total Amount</div>
                    <div className="text-xl font-bold text-slate-900">
                      R {formData.total.toLocaleString('en-ZA', { minimumFractionDigits: 2 })}
                    </div>
                  </div>
                </div>
              </div>

              {/* Notes Section */}
              <div className="mt-6">
                <label htmlFor="notes" className="block text-sm font-medium text-slate-700">
                  Notes
                </label>
                <textarea
                  id="notes"
                  value={formData.notes}
                  onChange={(e) => setFormData(prev => ({ ...prev, notes: e.target.value }))}
                  rows={3}
                  className="mt-1 block w-full border-gray-300 rounded-md shadow-sm focus:ring-cyan-500 focus:border-cyan-500 sm:text-sm"
                  placeholder="Additional notes or comments..."
                />
              </div>
        </BrandModalBody>

        <BrandModalFooter>
          <button type="button" onClick={onClose} className="btn btn-secondary text-sm">
            Cancel
          </button>
          <button
            type="submit"
            form="create-sale-form"
            disabled={loading || !formData.buyerName.trim() || items.every(item => !item.productName.trim())}
            className="btn btn-primary text-sm disabled:opacity-50 disabled:cursor-not-allowed"
          >
            {loading ? 'Creating...' : 'Create Sale'}
          </button>
        </BrandModalFooter>
      </form>
    </BrandModal>
  );
};

export default CreateSaleModal; 